import { randomUuid } from '@nzyme/crypto';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createEventEmitter } from '@nzyme/utils/createEventEmitter.js';
import { toJsonString } from '@nzyme/utils/toJsonString.js';
import { withTimeout } from '@nzyme/utils/withTimeout.js';
import { reactive } from '@nzyme/vue-utils/reactivity/reactive.js';
import { ref } from 'vue';
import * as z from 'zod/mini';
import { ActorEgress } from '@sigma/actors-core/ActorEgress.js';
import { ActorError } from '@sigma/actors-core/ActorError.js';
import { parseId } from '@sigma/actors-core/utils/parseId.js';
import { parseKey } from '@sigma/actors-core/utils/parseKey.js';
import { stringifyKey } from '@sigma/actors-core/utils/stringifyKey.js';
import { EMIT_EVENT } from './ActorClient.contract.js';
import { ActorClientRequestError } from './ActorClientRequestError.js';
const DEFAULT_REQUEST_TIMEOUT = 10000;
/**
 * Actor client service for managing WebSocket connections to the actor gateway.
 */
export const ActorClient = defineService({
    name: 'ActorClient',
    deps: {
        logger: Logger,
    },
    setup({ logger }) {
        const { event: disconnected, emit: emitDisconnected } = createEventEmitter();
        const { event: connected, emit: emitConnected } = createEventEmitter();
        const { event: beforeRequest, emitAsync: emitBeforeRequest } = createEventEmitter();
        let requestTimeout = DEFAULT_REQUEST_TIMEOUT;
        // Use Vue ref for status to make it reactive
        const statusRef = ref('DISCONNECTED');
        const state = {
            ws: undefined,
            status: statusRef,
            pendingRequests: new Map(),
            readyResolver: undefined,
        };
        // Cache actor refs by actor type
        const actorRefs = new Map();
        // Return a reactive object - Vue's reactive() unwraps refs automatically
        return reactive({
            status: statusRef,
            connected,
            disconnected,
            beforeRequest,
            connect,
            close,
            actor: getActorRef,
        });
        /**
         * Get or create an actor reference
         */
        function getActorRef(actor) {
            const existingRef = actorRefs.get(actor.type);
            if (existingRef) {
                return existingRef;
            }
            const actorRef = createActorRef(actor, state);
            actorRefs.set(actor.type, actorRef);
            return actorRef;
        }
        /**
         * Connect to the actor endpoint
         */
        async function connect(url, options) {
            requestTimeout = options?.requestTimeout ?? DEFAULT_REQUEST_TIMEOUT;
            if (state.status.value === 'CONNECTED' || state.status.value === 'CONNECTING') {
                logger.debug('Already connected or connecting');
                return;
            }
            try {
                state.status.value = 'CONNECTING';
                const wsUrl = buildWebSocketUrl(url, options?.query);
                state.ws = new WebSocket(wsUrl);
                // Set up handlers immediately so we can receive the RDY message
                setupWebSocketHandlers(state.ws);
                // Wait for RDY message from server (covers both WebSocket open and server ready)
                await waitForReady(state.ws, options?.connectionTimeout);
                state.status.value = 'CONNECTED';
                logger.debug('WebSocket connection established', { url: wsUrl.toString() });
                emitConnected();
            }
            catch (error) {
                logger.error('Failed to connect', { error });
                state.status.value = 'DISCONNECTED';
                state.ws?.close();
                state.ws = undefined;
                throw error;
            }
        }
        /**
         * Close the connection
         */
        function close() {
            if (!state.ws || state.status.value === 'DISCONNECTED') {
                logger.debug('Already disconnected');
                return Promise.resolve();
            }
            if (state.ws.readyState === WebSocket.CLOSED || state.ws.readyState === WebSocket.CLOSING) {
                logger.debug('WebSocket already closed or closing', { readyState: state.ws.readyState });
                return Promise.resolve();
            }
            logger.debug('Closing WebSocket connection');
            return new Promise(resolve => {
                if (!state.ws) {
                    resolve();
                    return;
                }
                const onSocketClosed = () => {
                    state.ws?.removeEventListener('close', onSocketClosed);
                    logger.info('WebSocket connection closed');
                    resolve();
                };
                state.ws.addEventListener('close', onSocketClosed);
                state.ws.close(1000, 'Connection closed by client');
            });
        }
        /**
         * Build WebSocket URL from provided URL and query params
         */
        function buildWebSocketUrl(url, query) {
            const wsUrl = new URL(url);
            // Convert http(s) to ws(s)
            if (wsUrl.protocol === 'https:') {
                wsUrl.protocol = 'wss:';
            }
            else if (wsUrl.protocol === 'http:') {
                wsUrl.protocol = 'ws:';
            }
            if (query) {
                for (const [key, value] of Object.entries(query)) {
                    wsUrl.searchParams.set(key, value);
                }
            }
            return wsUrl;
        }
        /**
         * Setup WebSocket event handlers
         */
        function setupWebSocketHandlers(socket) {
            socket.addEventListener('message', event => {
                void handleMessage(event);
            });
            socket.addEventListener('close', event => {
                handleClose(event);
            });
            socket.addEventListener('error', () => {
                logger.error('WebSocket error');
            });
        }
        /**
         * Handle incoming WebSocket message
         */
        async function handleMessage(event) {
            try {
                let text;
                if (typeof event.data === 'string') {
                    text = event.data;
                }
                else if (event.data instanceof ArrayBuffer) {
                    text = new TextDecoder().decode(event.data);
                }
                else if (event.data instanceof Blob) {
                    text = await event.data.text();
                }
                else {
                    logger.error('Received unsupported message type', { data: event.data });
                    return;
                }
                const message = z.parse(ActorEgress, JSON.parse(text));
                if (message.type === 'EVT') {
                    handleEvent(message);
                }
                else if (message.type === 'RES') {
                    handleResponse(message);
                }
                else if (message.type === 'RDY') {
                    handleReady(message);
                }
            }
            catch (error) {
                logger.error('Failed to process incoming message', { error });
            }
        }
        /**
         * Handle incoming event
         */
        function handleEvent(evt) {
            const parsedKey = parseKey(evt.actor);
            if (!parsedKey) {
                logger.error('Invalid actor key', { actor: evt.actor });
                return;
            }
            const actorRef = actorRefs.get(parsedKey.type);
            if (!actorRef) {
                logger.debug('No ref found for actor event', { actor: evt.actor });
                return;
            }
            const actorId = parseId(actorRef.actor, parsedKey.id);
            logger.debug('Received event', {
                actor: evt.actor,
                actorId,
                event: evt.event,
            });
            // Emit the event by name
            actorRef[EMIT_EVENT][evt.event]?.({
                actorId,
                event: evt.data,
                ts: evt.ts,
                hostId: evt.hostId,
            });
        }
        /**
         * Handle incoming response
         */
        function handleResponse(response) {
            const pending = state.pendingRequests.get(response.id);
            if (!pending) {
                logger.warn('Received response for unknown request', { id: response.id });
                return;
            }
            state.pendingRequests.delete(response.id);
            logger.debug('Received response', {
                id: response.id,
                status: response.status,
                durationMs: Date.now() - pending.sentAt,
            });
            pending.resolve(response);
        }
        /**
         * Handle incoming ready message
         */
        function handleReady(ready) {
            logger.debug('Received RDY message', { hostId: ready.hostId, ts: ready.ts });
            if (state.readyResolver) {
                state.readyResolver.resolve(ready);
                state.readyResolver = undefined;
            }
        }
        /**
         * Handle WebSocket close
         */
        function handleClose(event) {
            const previousStatus = state.status.value;
            state.status.value = 'DISCONNECTED';
            state.ws = undefined;
            logger.debug('WebSocket connection closed', {
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
            });
            const closeError = new ActorError('WebSocket connection closed', {
                closeCode: event.code,
                closeReason: event.reason,
                wasClean: event.wasClean,
            });
            // Reject ready resolver if waiting for RDY
            if (state.readyResolver) {
                state.readyResolver.reject(closeError);
                state.readyResolver = undefined;
            }
            // Reject all pending requests
            for (const [_id, pending] of state.pendingRequests) {
                pending.reject(closeError);
            }
            state.pendingRequests.clear();
            if (previousStatus === 'CONNECTED') {
                emitDisconnected();
            }
        }
        /**
         * Wait for RDY message from server.
         * This covers both WebSocket open and server ready - the RDY message
         * can only be received after the WebSocket is open and the server is ready.
         */
        function waitForReady(socket, timeout) {
            return new Promise((resolve, reject) => {
                let timeoutId;
                const handleError = () => {
                    cleanup();
                    reject(new ActorError('WebSocket connection failed', { readyState: socket.readyState }));
                };
                const handleTimeout = () => {
                    cleanup();
                    socket.close();
                    reject(new ActorError(`WebSocket connection timed out after ${timeout}ms`, {
                        timeout,
                        readyState: socket.readyState,
                    }));
                };
                const cleanup = () => {
                    socket.removeEventListener('error', handleError);
                    state.readyResolver = undefined;
                    if (timeoutId !== undefined) {
                        clearTimeout(timeoutId);
                    }
                };
                // Set up the ready resolver - handleReady() and handleClose() will use this
                state.readyResolver = {
                    resolve: ready => {
                        cleanup();
                        resolve(ready);
                    },
                    reject: error => {
                        cleanup();
                        reject(error);
                    },
                };
                socket.addEventListener('error', handleError);
                if (timeout !== undefined && timeout > 0) {
                    timeoutId = setTimeout(handleTimeout, timeout);
                }
            });
        }
        /**
         * Send a request to an actor
         */
        async function sendRequest(actor, requestName, actorId, input, clientState) {
            await emitBeforeRequest();
            if (!clientState.ws || clientState.status.value !== 'CONNECTED') {
                throw new ActorError('WebSocket is not connected', {
                    status: clientState.status.value,
                });
            }
            const id = randomUuid();
            const actorKey = stringifyKey(actor.type, actorId);
            const sentAt = Date.now();
            const request = {
                type: 'REQ',
                id,
                actor: actorKey,
                request: requestName,
                data: input,
                ts: sentAt,
            };
            const json = toJsonString(request);
            logger.debug('Sending request', {
                id,
                actor: actorKey,
                request: requestName,
            });
            // Send the request
            clientState.ws.send(json);
            // Wait for the response. The request is already on the wire, so a timeout only stops
            // tracking it locally — a response arriving late is dropped with its map entry gone.
            return await withTimeout({
                operation: () => new Promise((resolve, reject) => {
                    clientState.pendingRequests.set(id, {
                        resolve: response => {
                            if (response.status === 200) {
                                resolve({
                                    result: response.data,
                                    ts: response.ts,
                                    hostId: response.hostId,
                                });
                            }
                            else {
                                reject(new ActorClientRequestError(response.error || `Request failed with status ${response.status}`, {
                                    requestId: id,
                                    actorKey,
                                    actorType: actor.type,
                                    requestName,
                                    sentAt,
                                    durationMs: Date.now() - sentAt,
                                    hostId: response.hostId,
                                    status: response.status,
                                    responseError: response.error,
                                    responseData: response.data,
                                }));
                            }
                        },
                        reject,
                        sentAt,
                    });
                }),
                timeoutMs: requestTimeout,
                onTimeout: () => {
                    clientState.pendingRequests.delete(id);
                    throw new ActorClientRequestError(`Request timed out after ${requestTimeout}ms`, {
                        requestId: id,
                        actorKey,
                        actorType: actor.type,
                        requestName,
                        sentAt,
                        durationMs: Date.now() - sentAt,
                    });
                },
            });
        }
        /**
         * Create an actor reference
         */
        function createActorRef(actor, clientState) {
            // Build requests object with individual functions for each request type
            const requests = {};
            const requestNames = Object.keys(actor.requests);
            for (const requestName of requestNames) {
                requests[requestName] = ((actorId, input) => {
                    return sendRequest(actor, requestName, actorId, input, clientState);
                });
            }
            // Build events object with individual emitters for each event type
            const eventEmitters = {};
            const events = {};
            const eventNames = Object.keys(actor.events);
            for (const eventName of eventNames) {
                const { event, emit } = createEventEmitter();
                events[eventName] = event;
                eventEmitters[eventName] = emit;
            }
            const actorRef = {
                actor,
                requests,
                events,
                [EMIT_EVENT]: eventEmitters,
            };
            return actorRef;
        }
    },
});
//# sourceMappingURL=ActorClient.js.map